import { normalizeApiError } from '../api/apiError';
import { createReadAsyncThunk } from "../store/createReadAsyncThunk";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FAMDetails } from "../models/membership/famDetails";
import { Fam } from '../api/endpoints/famApi';
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { FamMembershipSubscription } from "../models/membership/famMembershipSubscription";
import { FAMOnboarding } from "../models/membership/famOnboarding";
import { FAMMembershipType } from "../models/membership/famMembershipType";

interface famState {
    famDetails: FAMDetails | null;
    famMembershipSubscriptions: FamMembershipSubscription[] | null;
    currentYear: number | null;
    famOnboardingDetails: FAMOnboarding[] | null;
    currentMembershipType: FAMMembershipType | null;
    canUseDirectDebitMandate: boolean | null;

    famDetailsStatus: ApiRequestStatus;
    famOnboardingDetailsStatus: ApiRequestStatus;
    updateFamDetailsStatus: ApiRequestStatus;
    addFamDetailsStatus: ApiRequestStatus;
    famSubscriptionsStatus: ApiRequestStatus;
    currentYearStatus: ApiRequestStatus;
    currentMembershipTypeStatus: ApiRequestStatus;
    canUseDirectDebitMandateStatus: ApiRequestStatus;
}

const initialState: famState = {
    famDetails: null,
    famMembershipSubscriptions: null,
    currentYear: null,
    famOnboardingDetails: null,
    currentMembershipType: null,
    canUseDirectDebitMandate: null,

    famDetailsStatus: ApiRequestStatus.Idle,
    updateFamDetailsStatus: ApiRequestStatus.Idle,
    addFamDetailsStatus: ApiRequestStatus.Idle,
    famSubscriptionsStatus: ApiRequestStatus.Idle,
    currentYearStatus: ApiRequestStatus.Idle,
    famOnboardingDetailsStatus: ApiRequestStatus.Idle,
    currentMembershipTypeStatus: ApiRequestStatus.Idle,
    canUseDirectDebitMandateStatus: ApiRequestStatus.Idle
}

export const getFAMDetails = createReadAsyncThunk<FAMDetails, string>(
    'famSlice/getFAMDetails',
    async (personId, thunkAPI) => {
        try {
            return await Fam.getFAMDetails(personId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getFamSubscriptionsAsync = createReadAsyncThunk<FamMembershipSubscription[]>(
    'famSlice/getFamSubscriptionsAsync',
    async (_, thunkAPI) => {
        try {
            return await Fam.getFamSubscriptionsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const updateFAMDetails = createAsyncThunk<boolean, FAMDetails>(
    'famSlice/updateFAMDetails',
    async (famDetails, thunkAPI) => {
        try {
            return await Fam.updateFamDetails(famDetails);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const addFAMDetails = createAsyncThunk<boolean, FAMDetails>(
    'famSlice/addFAMDetails',
    async (famDetails, thunkAPI) => {
        try {
            return await Fam.addFamDetails(famDetails);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getCurrentYearAsync = createReadAsyncThunk<number>(
    'famSlice/getCurrentYearAsync',
    async (_, thunkAPI) => {
        try {
            return await Fam.getCurrentYear();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getFamExamsAsync = createReadAsyncThunk<FAMOnboarding[]>(
    'famSlice/getFamExamsAsync',
    async (_, thunkAPI) => {
        try {
            return await Fam.getFamExamDetailsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getCurrentMembershipType = createReadAsyncThunk<FAMMembershipType>(
    'famSlice/getCurrentMembershipType',
    async (_, thunkAPI) => {
        try {
            return await Fam.getCurrentMembershipTypeAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getCanUseDirectDebitMandateAsync = createReadAsyncThunk<boolean>(
    'famSlice/getCanUseDirectDebitMandateAsync',
    async (_, thunkAPI) => {
        try {
            return await Fam.getCanUseDirectDebitMandateAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const famSlice = createSlice({
    name: 'famSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(updateFAMDetails.pending, (state) => {
            state.updateFamDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(updateFAMDetails.fulfilled, (state, action) => {
            state.updateFamDetailsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(updateFAMDetails.rejected, (state) => {
            state.updateFamDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(addFAMDetails.pending, (state) => {
            state.addFamDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(addFAMDetails.fulfilled, (state) => {
            state.addFamDetailsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(addFAMDetails.rejected, (state) => {
            state.addFamDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getFAMDetails.pending, (state) => {
            state.famDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getFAMDetails.fulfilled, (state, action) => {
            state.famDetails = action.payload;
            state.famDetailsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getFAMDetails.rejected, (state) => {
            state.famDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getFamSubscriptionsAsync.pending, (state) => {
            state.famSubscriptionsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getFamSubscriptionsAsync.fulfilled, (state, action) => {
            state.famMembershipSubscriptions = action.payload;
            state.famSubscriptionsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getFamSubscriptionsAsync.rejected, (state) => {
            state.famSubscriptionsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getCurrentYearAsync.pending, (state) => {
            state.currentYearStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getCurrentYearAsync.fulfilled, (state, action) => {
            state.currentYear = action.payload;
            state.currentYearStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getCurrentYearAsync.rejected, (state) => {
            state.currentYearStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getFamExamsAsync.pending, (state) => {
            state.famOnboardingDetailsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getFamExamsAsync.fulfilled, (state, action) => {
            state.famOnboardingDetailsStatus = ApiRequestStatus.Fulfilled;
            state.famOnboardingDetails = action.payload;
        });
        builder.addCase(getFamExamsAsync.rejected, (state) => {
            state.famOnboardingDetailsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getCurrentMembershipType.pending, (state) => {
            state.currentMembershipTypeStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getCurrentMembershipType.fulfilled, (state, action) => {
            state.currentMembershipType = action.payload;
            state.currentMembershipTypeStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getCurrentMembershipType.rejected, (state) => {
            state.currentMembershipTypeStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getCanUseDirectDebitMandateAsync.pending, (state) => {
            state.canUseDirectDebitMandateStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getCanUseDirectDebitMandateAsync.fulfilled, (state, action) => {
            state.canUseDirectDebitMandate = action.payload;
            state.canUseDirectDebitMandateStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getCanUseDirectDebitMandateAsync.rejected, (state) => {
            state.canUseDirectDebitMandateStatus = ApiRequestStatus.Rejected;
        });
    })
})
